import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Buffer } from 'buffer';
import { useTranslation } from 'react-i18next';

import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { ActionButtons } from '../../others/ActionButtons';
import LoadingOverlay from '../../others/LoadingOverlay';
import DynamicForm from '../../dynamicForm/DynamicForm';

import { useValidateDynamicForm } from '../../../hooks/useValidateDynamicForm';
import { useNotification } from '../../../hooks/useNotification';
import { useDataAuth } from '../../../hooks/useDataAuth';
import { useApiResolver } from '../../../hooks/useApiResolver';

import { isNotEmptyObject } from '../../../utils/jsonValidator';
import { ActionIconItem } from '../../others/ActionIconItem';
import { useForm } from '../../../hooks/useForm';
import { useResolveBodyData } from '../../../hooks/useResolveBodyData';
import { serializeSchemeValues } from '../../../helpers/configResolver';

const baseUrl = process.env.REACT_APP_AWS_API_GATEWAY_URL_EXECUTE_FLOW;

export const ModalForms = ({ data, dtoReg, handleChange, companyInfo = {} }) => {
    const { t } = useTranslation();
    const { resolveValue } = useApiResolver();
    const [{ userSession }] = useDataAuth();
    const [errorInputs, validForm, resetErrors] = useValidateDynamicForm();
    const [sendNotification] = useNotification();
    const [open, setOpen] = useState(false);
    const [isLoading, setisLoading] = useState(false);
    const isSubmittingRef = useRef(false);
    const [fileBytes, setFileBytes] = useState(null);
    const [file, setFile] = useState(null);

    const theme = useTheme();
    useMediaQuery(theme.breakpoints.down('sm'));

    const { resolveInfo, resetInit, templateHasPlaceholder } = useResolveBodyData(data.trigger[0].bodyData);
    const formFieldIds = new Set((data?.form || []).map((field) => field.id));
    const [formData, handleInputChange, handleDirectInputChange, , reset] = useForm(
        data?.form?.reduce((json, element) => {
            if (Object.prototype.hasOwnProperty.call(dtoReg, element.id) && dtoReg[element.id] != null) {
                json[element.id] = typeof dtoReg[element.id] === 'object'
                    ? dtoReg[element.id]
                    : String(dtoReg[element.id]);
            }
            return json;
        }, { id: dtoReg.id }) || { id: dtoReg.id }
    );

    const setValuesSelectGeneric = (e) => {
        const { name, value, fullObject } = e.target;
        const updates = { [name]: value };
        if (isNotEmptyObject(fullObject)) {
            const reservedKeys = new Set(['id', 'code']);
            for (const key of Object.keys(fullObject)) {
                const prefixed = `${name}_${key}`;
                if (templateHasPlaceholder(prefixed) || formFieldIds.has(prefixed)) {
                    updates[prefixed] = fullObject[key];
                }
                if (
                    key !== name &&
                    !reservedKeys.has(key) &&
                    (formFieldIds.has(key) || templateHasPlaceholder(key))
                ) {
                    updates[key] = fullObject[key];
                }
            }
        }
        handleDirectInputChange(updates);
    }

    const setFileTemp = (bytes, field) => {
        const u8 = new Uint8Array(bytes);
        const b64 = Buffer.from(u8).toString('base64');
        handleDirectInputChange(field + '_file_B64', b64);
        setFileBytes(bytes);
    };

    const handleFileChange = (event, field) => {
        const fileSelected = event.target.files[0];
        setFile(fileSelected);
        handleDirectInputChange(field, fileSelected?.name);

        if (fileSelected) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const bytes = new Uint8Array(e.target.result);
                setFileTemp(bytes, field);
            };
            reader.readAsArrayBuffer(fileSelected);
        }
        event.target.value = '';
    };

    const handleUploadClick = async () => {
        if (!fileBytes) return;

        try {
            const u8 = new Uint8Array(fileBytes);
            const b64 = Buffer.from(u8).toString('base64');

            const bodyData = {
                id_flow: "260",
                id: dtoReg.id,
                name: file.name.replace(/\s+/g, '_'),
                file: b64
            };

            const response = await axios.post(baseUrl, bodyData, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userSession.token}`
                },
            });

            if (response.status === 200) {
                if (isNotEmptyObject(response.data)) {
                    handleDirectInputChange(response.data);
                }
                sendNotification({ msg: t("messages.fileUploadSuccess"), variant: 'info' });
            } else {
                sendNotification({ msg: t("messages.saveError"), variant: 'error' });
            }
        } catch (error) {
            sendNotification({ msg: t("messages.saveError"), variant: 'error' });
        }
    };

    const CallApi = async (param) => {
        if (isSubmittingRef.current) return;
        if (!validForm(data.form, formData)) return;

        isSubmittingRef.current = true;
        setisLoading(true);

        try {
            const { url, method } = param[0];
            // Priority: 1) form (incl. cleared fields), 2) record (dtoReg), 3) companyInfo
            const body = resolveInfo(
                serializeSchemeValues(formData, data.form),
                null,
                true,
                serializeSchemeValues({ ...companyInfo, ...dtoReg }, data.form)
            );
            if (companyInfo.id_company) {
                body.id_company = companyInfo.id_company;
                body.select_company = companyInfo.select_company;
            }

            const { success, errorCode } = await resolveValue({
                method,
                url,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userSession.token}`,
                },
                data: JSON.stringify(body),
            });
            if (success && !errorCode) {
                setOpen(false);
                setTimeout(() => handleChange(), 500);
            }
        } finally {
            isSubmittingRef.current = false;
            setisLoading(false);
        }
    };

    const handleOpenModal = (isOpen) => {
        setOpen(isOpen);
        resetErrors();
        reset();
        resetInit();
    };

    return (
        <>
            <ActionIconItem
                item={data}
                onClick={() => handleOpenModal(true)}
                defaultIcon="DynamicForm"
                badgeContent={data?.badge || null}
            />

            <Dialog
                open={open}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        width: {
                            xs: '100%',
                            md: data?.width ? `${data.width}%` : '35%'
                        },
                        margin: { xs: 1, md: 0 },
                        maxHeight: '90vh',
                        borderRadius: 2,
                    }
                }}
            >
                {/* ---------- HEADER ---------- */}
                <DialogTitle sx={{ borderBottom: '1px solid #ccc' }}>
                    <Typography variant="h6">{data.accion}</Typography>
                </DialogTitle>

                {/* ---------- BODY ---------- */}
                <DialogContent sx={{ overflowY: 'auto', flexGrow: 1 }}>
                    <DynamicForm
                        values={formData}
                        scheme={data.form}
                        errorInputs={errorInputs}
                        onChange={handleInputChange}
                        handleFileChange={handleFileChange}
                        handleUploadClick={handleUploadClick}
                        setChanges={handleDirectInputChange}
                        setValuesSelectGeneric={setValuesSelectGeneric}
                        companyInfo={companyInfo}
                    />
                </DialogContent>

                {/* ---------- FOOTER ---------- */}
                <DialogActions sx={{ borderTop: '1px solid #ccc' }}>
                    <ActionButtons
                        onSubmit={() => CallApi(data.trigger)}
                        onCancel={() => handleOpenModal(false)}
                        disabledSubmit={isLoading}
                        disabledCancel={isLoading}
                    />
                </DialogActions>

                <LoadingOverlay
                    isOpen={isLoading}
                    text="Esperando ..."
                    contained
                />
            </Dialog>
        </>
    );
};
